import {
  Badge,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ListItemIcon,
  ListItemText,
  MenuItem,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import NotificationsIcon from "@mui/icons-material/Notifications";
import CloseIcon from "@mui/icons-material/Close";
import { Dispatch, SetStateAction, useState } from "react";
import { readNotifications } from "../actions/notification.action";
import { downloadSentiments } from "../actions/sentiment.action";
import { openWindowInAsync } from "../actions/common.action";
import {
  INotification,
  NotificationStatus,
  NotificationType,
} from "../models/notification.model";
import { useConfig } from "../containers/ConfigContext";

interface NotificationMenuItemProps {
  notifications: INotification[];
  setNotifications: Dispatch<SetStateAction<INotification[]>>;
}

export default function NotificationMenuItem({
  notifications,
  setNotifications,
}: NotificationMenuItemProps) {
  const [open, setOpen] = useState(false);
  const unreadCount = notifications.filter(
    (n) => n.status === NotificationStatus.unread
  ).length;
  const { localized } = useConfig();

  const handleClick = async () => {
    setOpen(true);
  };

  const handleRead = async (ids: string[]) => {
    setNotifications((prev) =>
      prev.map((n) =>
        ids.includes(n.id) ? { ...n, status: NotificationStatus.read } : n
      )
    );

    // Call API in background (fire and forget)
    readNotifications(ids).catch((error) => {
      console.error("Failed to mark notifications as read:", error);
    });
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDownload = async (notification: INotification) => {
    handleRead([notification.id]);

    try {
      const key = notification.metadata?.key;
      if (!key) {
        window.webApp.showMessage("error", "Download key not found");
        return;
      }

      await openWindowInAsync(async () => {
        const result = await downloadSentiments(encodeURIComponent(key));
        return result.url;
      }, "Preparing download...");
    } catch (error: any) {
      window.webApp.showMessage(
        "error",
        `Download failed: ${error?.message || error}`
      );
    }
  };

  return (
    <>
      <MenuItem onClick={handleClick}>
        <ListItemIcon>
          <Badge badgeContent={unreadCount} color="success">
            <NotificationsIcon />
          </Badge>
        </ListItemIcon>
        <ListItemText>{localized("Notifications")}</ListItemText>
      </MenuItem>

      <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
        <DialogTitle>
          {localized("Notifications")}
          <IconButton
            aria-label="close"
            onClick={handleClose}
            sx={{
              position: "absolute",
              right: 8,
              top: 8,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>{localized("Title")}</TableCell>
                  <TableCell>{localized("Description")}</TableCell>
                  <TableCell align="center">{localized("Action")}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {notifications.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={3} align="center">
                      {localized("No notifications")}
                    </TableCell>
                  </TableRow>
                ) : (
                  notifications.map((notification) => (
                    <TableRow
                      key={notification.id}
                      sx={{
                        backgroundColor:
                          notification.status === NotificationStatus.unread
                            ? "rgba(76, 175, 80, 0.08)"
                            : "transparent",
                      }}
                    >
                      <TableCell>{notification.title}</TableCell>
                      <TableCell>{notification.description}</TableCell>
                      <TableCell align="center">
                        {notification.type === NotificationType.download && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => handleDownload(notification)}
                          >
                            {localized("Download")}
                          </Button>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>{localized("Close")}</Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
